import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchIcon } from 'foundations-assets';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider, useTheme } from 'styled-components';
import { useForwardedRef } from '../../hooks/useForwardedRefs';
import { usePrevious } from '../../hooks/usePrevious';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SEARCH_HEIGHT = '40px';
const IconButtonWrapper = styled.div.withConfig({
  displayName: "MobileSearchBar__IconButtonWrapper",
  componentId: "wipx1j-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    borderRadius: `0 ${theme.borderRadius['100']} ${theme.borderRadius['100']} 0 !important`,
    marginLeft: '-4px',
    width: '50px',
    borderLeftWidth: '0 !important',
    zIndex: '1',
    height: SEARCH_HEIGHT,
    display: 'flex',
    // Make children fill the entire container space
    '& > *': {
      width: '100%',
      height: '100% ',
      flex: '1 1 auto'
    }
  };
});
const SearchInputWrapper = styled.div.withConfig({
  displayName: "MobileSearchBar__SearchInputWrapper",
  componentId: "wipx1j-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    flex: 1,
    display: 'flex',
    borderRadius: `${theme.borderRadius['100']} 0 0 ${theme.borderRadius['100']}`,
    paddingRight: theme.space['0'],
    height: SEARCH_HEIGHT,
    borderRight: '0',
    '& input:focus': {
      // Target input elements within this container
      borderRight: '0',
      boxShadow: 'none',
      outline: 'none' // Also remove the browser's default outline
    }
  };
});
const CancelButtonWrapper = styled.div.withConfig({
  displayName: "MobileSearchBar__CancelButtonWrapper",
  componentId: "wipx1j-2"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    color: theme.color['text-core-on-fill-default'],
    right: theme.space['200'],
    lineHeight: SEARCH_HEIGHT,
    marginLeft: theme.space['200']
  };
});
const StyledPlaceholderButton = styled.div.withConfig({
  displayName: "MobileSearchBar__StyledPlaceholderButton",
  componentId: "wipx1j-3"
})(["width:100%;"]);
function MobileSearchBar({
  ariaLabel,
  buttonFocused,
  cancelSearch,
  inputValue,
  onClear,
  onClick,
  onSearchFocus,
  onSearchUpdate,
  focused,
  onSearchSubmit,
  placeholder,
  use
}, ref) {
  const [searchInputValue, setSearchInputValue] = useState(inputValue || '');
  const prevInputValue = usePrevious(searchInputValue);
  const searchRef = useForwardedRef(ref);
  const buttonRef = useForwardedRef(ref);
  useEffect(() => {
    setSearchInputValue(inputValue || '');
  }, [inputValue]);
  useEffect(() => {
    if (!searchInputValue && prevInputValue) {
      onClear();
    }
  }, [searchInputValue, prevInputValue, onClear]);
  useEffect(() => {
    if (buttonFocused && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [buttonRef, buttonFocused]);
  const handleSubmitSearch = useCallback(evt => {
    evt.preventDefault();
    onSearchSubmit(searchInputValue);
    if (searchRef.current) {
      searchRef.current.blur();
    }
  }, [searchInputValue, searchRef, onSearchSubmit]);
  const focusSearchBar = useCallback(evt => {
    if (onSearchFocus) onSearchFocus(evt);
  }, [onSearchFocus]);
  const renderedPlaceholderSearchBar = useMemo(() => {
    return /*#__PURE__*/_jsxs(TFlex, {
      children: [/*#__PURE__*/_jsx(StyledPlaceholderButton, {
        children: /*#__PURE__*/_jsx(Clickable, {
          "aria-label": ariaLabel,
          buttonRef: buttonRef,
          onClick: onClick,
          children: /*#__PURE__*/_jsx(SearchInputWrapper, {
            children: /*#__PURE__*/_jsx(SearchInput, {
              clearable: false,
              icon: false,
              placeholder: placeholder,
              tabIndex: -1,
              value: searchInputValue
            })
          })
        })
      }), /*#__PURE__*/_jsx(IconButtonWrapper, {
        children: /*#__PURE__*/_jsx(IconButton, {
          "aria-label": unescapedText('marketplace-ui-common.search.searchBar.accessibleButtonName'),
          onClick: handleSubmitSearch,
          block: true,
          children: /*#__PURE__*/_jsx(SearchIcon, {
            color: 'fill-info-default'
          })
        })
      })]
    });
  }, [ariaLabel, buttonRef, handleSubmitSearch, onClick, placeholder, searchInputValue]);
  const theme = useTheme();
  if (use === 'placeholder') {
    return /*#__PURE__*/_jsx(ThemeProvider, {
      theme: theme,
      children: renderedPlaceholderSearchBar
    });
  }
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: theme,
    children: /*#__PURE__*/_jsx(Form, {
      action: ".",
      onSubmit: handleSubmitSearch,
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": ariaLabel,
        children: /*#__PURE__*/_jsxs(TFlex, {
          children: [/*#__PURE__*/_jsxs(TFlex, {
            children: [/*#__PURE__*/_jsx(SearchInputWrapper, {
              children: /*#__PURE__*/_jsx(SearchInput, {
                clearable: focused,
                icon: false,
                inputRef: searchRef,
                onChange: event => {
                  const value = event.target.value;
                  setSearchInputValue(value);
                  if (onSearchUpdate) {
                    onSearchUpdate(value);
                  }
                },
                onFocus: focusSearchBar,
                placeholder: placeholder,
                value: searchInputValue
              })
            }), /*#__PURE__*/_jsx(IconButtonWrapper, {
              children: /*#__PURE__*/_jsx(IconButton, {
                "aria-label": unescapedText('marketplace-ui-common.search.searchBar.accessibleButtonName'),
                onClick: handleSubmitSearch,
                children: /*#__PURE__*/_jsx(SearchIcon, {
                  color: 'fill-info-default'
                })
              })
            })]
          }), focused && /*#__PURE__*/_jsx(CancelButtonWrapper, {
            children: /*#__PURE__*/_jsx(Clickable, {
              onClick: cancelSearch,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-ui-common.search.searchBar.cancelButton"
              })
            })
          })]
        })
      })
    })
  });
}
export default /*#__PURE__*/forwardRef(MobileSearchBar);